import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ROIResults = ({ calculationData, onExportPDF, onScheduleConsultation }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Guard clause to prevent errors if calculationData is not available
  if (!calculationData) {
    return (
      <div className="bg-card rounded-xl shadow-executive p-6">
        <div className="text-center">
          <Icon name="AlertTriangle" size={48} color="var(--color-text-secondary)" />
          <h3 className="font-headline font-semibold text-lg text-text-primary mt-4">
            No Calculation Data Available
          </h3>
          <p className="text-text-secondary">
            Please complete the ROI calculator form to see results.
          </p>
        </div>
      </div>
    );
  }

  // Mock calculation results based on input data
  const calculateResults = () => {
    const currentHeadcount = parseInt(calculationData?.currentHeadcount) || 0;
    const averageSalary = parseInt(calculationData?.averageSalary) || 0;
    const operationalCosts = parseInt(calculationData?.operationalCosts) || 0;
    const desiredHeadcount = parseInt(calculationData?.desiredHeadcount) || 0;
    
    const currentCosts = (currentHeadcount * averageSalary) + operationalCosts;
    const gccCosts = {
      setup: 150000,
      yearlyOperational: (desiredHeadcount * 25000) + 200000,
      salaryReduction: 0.6 // 60% cost reduction
    };
    
    const yearlySavings = currentCosts - gccCosts.yearlyOperational;
    const fiveYearSavings = (yearlySavings * 5) - gccCosts.setup;
    const roi = currentCosts > 0 ? ((fiveYearSavings / (currentCosts * 5)) * 100).toFixed(1) : '0.0';
    const paybackPeriod = yearlySavings > 0 ? (gccCosts.setup / yearlySavings * 12).toFixed(1) : 'N/A';

    return {
      currentCosts,
      gccCosts,
      yearlySavings,
      fiveYearSavings,
      roi,
      paybackPeriod
    };
  };

  const results = calculateResults();

  const yearlyProjection = [
    { year: 'Year 1', currentCost: results.currentCosts, gccCost: results.gccCosts.yearlyOperational + results.gccCosts.setup, savings: results.yearlySavings - results.gccCosts.setup },
    { year: 'Year 2', currentCost: results.currentCosts, gccCost: results.gccCosts.yearlyOperational, savings: results.yearlySavings },
    { year: 'Year 3', currentCost: results.currentCosts, gccCost: results.gccCosts.yearlyOperational, savings: results.yearlySavings },
    { year: 'Year 4', currentCost: results.currentCosts, gccCost: results.gccCosts.yearlyOperational, savings: results.yearlySavings },
    { year: 'Year 5', currentCost: results.currentCosts, gccCost: results.gccCosts.yearlyOperational, savings: results.yearlySavings }
  ];

  const cumulativeSavings = [
    { year: 'Year 1', savings: results.yearlySavings - results.gccCosts.setup },
    { year: 'Year 2', savings: (results.yearlySavings * 2) - results.gccCosts.setup },
    { year: 'Year 3', savings: (results.yearlySavings * 3) - results.gccCosts.setup },
    { year: 'Year 4', savings: (results.yearlySavings * 4) - results.gccCosts.setup },
    { year: 'Year 5', savings: results.fiveYearSavings }
  ];

  const costBreakdown = [
    { name: 'Salary Costs', value: results.gccCosts.yearlyOperational * 0.7, color: '#1a2332' },
    { name: 'Infrastructure', value: results.gccCosts.yearlyOperational * 0.15, color: '#2c3e50' },
    { name: 'Operations', value: results.gccCosts.yearlyOperational * 0.1, color: '#d4af37' },
    { name: 'Compliance', value: results.gccCosts.yearlyOperational * 0.05, color: '#6c757d' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'projections', label: 'Projections', icon: 'TrendingUp' },
    { id: 'breakdown', label: 'Cost Breakdown', icon: 'PieChart' },
    { id: 'assumptions', label: 'Assumptions', icon: 'FileText' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="bg-card rounded-xl shadow-executive">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 gradient-accent rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} color="white" />
            </div>
            <div>
              <h3 className="font-headline font-semibold text-lg text-text-primary">
                ROI Analysis Results
              </h3>
              <p className="text-text-secondary text-sm">
                Based on your inputs for {calculationData?.serviceType || 'N/A'} in {calculationData?.location || 'N/A'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="default"
              size="sm"
              onClick={onScheduleConsultation}
              className="bg-cta hover:bg-cta/90 text-cta-foreground"
              iconName="Calendar"
              iconPosition="left"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="p-6 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-accent mb-1">
              {results.roi}%
            </div>
            <div className="text-sm text-text-secondary">5-Year ROI</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-text-primary mb-1">
              {formatCurrency(results.fiveYearSavings)}
            </div>
            <div className="text-sm text-text-secondary">Total Savings</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-success mb-1">
              {formatCurrency(results.yearlySavings)}
            </div>
            <div className="text-sm text-text-secondary">Annual Savings</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-secondary mb-1">
              {results.paybackPeriod}
            </div>
            <div className="text-sm text-text-secondary">Payback (Months)</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex space-x-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-colors duration-200 border-b-2 ${
                activeTab === tab.id
                  ? 'border-accent text-accent bg-accent/5' :'border-transparent text-text-secondary hover:text-text-primary hover:bg-muted/50'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Cost Comparison</h4>
              <div className="w-full h-80 flex items-center justify-center">
                <BarChart width={Math.min(window.innerWidth - 100, 800)} height={320} data={yearlyProjection}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="year" stroke="var(--color-text-secondary)" />
                    <YAxis stroke="var(--color-text-secondary)" tickFormatter={formatCurrency} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="currentCost" fill="var(--color-error)" name="Current Cost" />
                    <Bar dataKey="gccCost" fill="var(--color-primary)" name="GCC Cost" />
                  </BarChart>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'projections' && (
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Cumulative Savings Over Time</h4>
              <div className="w-full h-80 flex items-center justify-center">
                <LineChart width={Math.min(window.innerWidth - 100, 800)} height={320} data={cumulativeSavings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="year" stroke="var(--color-text-secondary)" />
                    <YAxis stroke="var(--color-text-secondary)" tickFormatter={formatCurrency} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="savings" 
                      stroke="var(--color-accent)" 
                      strokeWidth={3}
                      dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 6 }}
                    />
                  </LineChart>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'breakdown' && (
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Annual GCC Cost Breakdown</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-80 flex items-center justify-center">
                  <PieChart width={400} height={320}>
                      <Pie
                        data={costBreakdown}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {costBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                </div>
                
                <div className="space-y-4">
                  {costBreakdown.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-text-primary font-medium">{item.name}</span>
                      </div>
                      <span className="text-text-secondary">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'assumptions' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-text-primary">Cost Assumptions</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Salary Cost Reduction</span>
                    <span className="font-medium text-text-primary">60%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Setup Investment</span>
                    <span className="font-medium text-text-primary">{formatCurrency(results.gccCosts.setup)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Infrastructure Overhead</span>
                    <span className="font-medium text-text-primary">15%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Compliance & Legal</span>
                    <span className="font-medium text-text-primary">5%</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-text-primary">Market Factors</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Location Premium</span>
                    <span className="font-medium text-text-primary">
                      {calculationData?.location === 'bangalore' ? '+15%' : 
                       calculationData?.location === 'mumbai' ? '+20%' : 
                       calculationData?.location === 'hyderabad' ? '+5%' : 'Standard'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Service Type Factor</span>
                    <span className="font-medium text-text-primary">
                      {calculationData?.serviceType === 'software-development' ? 'High Skill' :
                       calculationData?.serviceType === 'data-analytics' ? 'Premium' :
                       calculationData?.serviceType === 'customer-support' ? 'Standard' : 'Specialized'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Timeline Impact</span>
                    <span className="font-medium text-text-primary">
                      {calculationData?.timeline === '6-months' ? 'Rush Premium' :
                       calculationData?.timeline === '12-months' ? 'Optimal' :
                       calculationData?.timeline === '18-months' ? 'Phased Savings' : 'Maximum Optimization'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-text-secondary">Currency Stability</span>
                    <span className="font-medium text-text-primary">±5% Annual</span>
                  </div>
                </div>
              </div>
            </div>
            
            <details className="bg-card border border-border rounded-lg p-4">
              <summary className="cursor-pointer font-medium text-text-primary flex items-center">
                <Icon name="BookOpen" size={18} className="mr-2" />
                Sources &amp; methodology
              </summary>
              <div className="mt-4 space-y-3 text-sm text-text-secondary">
                <p><strong className="text-text-primary">Cost-savings range:</strong> India GCC operations typically deliver 50–60% cost savings vs US baselines per the <a href="https://zinnov.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Zinnov GCC Cost Index 2024</a> and Everest Group public benchmarks. We tune the function-specific rate by role (engineering, customer support, finance, etc.) to reflect the talent premium curve.</p>
                <p><strong className="text-text-primary">City cost multipliers:</strong> Relative cost indices vs Bangalore baseline (= 100) compiled from Mercer Total Remuneration Survey India 2024 and state IT/ITeS policy briefs via Invest India. Tier-2 city values are directional estimates, not survey-grade extracts.</p>
                <p><strong className="text-text-primary">Salary benchmarks:</strong> <a href="https://www.mercer.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mercer 2024</a>, <a href="https://www.aon.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Aon India Salary Increase Survey 2024</a>, <a href="https://www.naukri.com/jobspeak" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Naukri JobSpeak Index</a>.</p>
                <p><strong className="text-text-primary">Setup &amp; operating cost model:</strong> Bootminds team engagement data (2018–present) plus Zinnov / NASSCOM-Zinnov public benchmarks. Numbers shown are ranges, not point estimates.</p>
              </div>
            </details>

            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
                <div>
                  <h5 className="font-medium text-text-primary mb-1">What this estimate excludes</h5>
                  <ul className="text-sm text-text-secondary list-disc list-inside space-y-1">
                    <li>Change-management and parallel-run costs in your home market</li>
                    <li>One-time legal entity, banking, and registrations beyond Bootminds&apos; Entity-in-a-Box™ scope</li>
                    <li>Currency hedging, transfer pricing benchmarking, and ongoing TP audits</li>
                    <li>Office space, fit-out, and IT capex above the standard zero-trust workstation pack</li>
                    <li>Talent retention bonuses, ESOP allocations, and equity-linked compensation</li>
                  </ul>
                  <p className="text-sm text-text-secondary mt-2">For a calibrated, scope-specific model, request the full ROI report below.</p>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-5">
              <h5 className="font-semibold text-text-primary mb-1 flex items-center">
                <Icon name="Mail" size={18} className="mr-2" />
                Email me the full ROI report (PDF)
              </h5>
              <p className="text-sm text-text-secondary mb-3">We&apos;ll send a calibrated ROI model with sources, sensitivity ranges, and a scope-specific cost breakdown to your inbox within one business day.</p>
              <form
                className="flex flex-col sm:flex-row gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const payload = {
                    name: formData.get('name'),
                    email: formData.get('email'),
                    company: formData.get('company'),
                    calculationData
                  };
                  // Submit via Web3Forms (existing integration)
                  fetch('https://api.web3forms.com/submit', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({
                      access_key: import.meta.env.VITE_WEB3FORMS_KEY || '',
                      subject: `ROI report request — ${payload.company || payload.email}`,
                      from_name: payload.name,
                      from_email: payload.email,
                      message: `ROI report request for ${payload.company}\n\nCalculation inputs:\n${JSON.stringify(payload.calculationData, null, 2)}`
                    })
                  }).then(() => {
                    e.target.reset();
                    e.target.querySelector('[data-success]').classList.remove('hidden');
                  }).catch(() => {
                    e.target.querySelector('[data-error]').classList.remove('hidden');
                  });
                }}
              >
                <input name="name" required placeholder="Full name" className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm" />
                <input name="email" type="email" required placeholder="Work email" className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm" />
                <input name="company" required placeholder="Company" className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm" />
                <button type="submit" className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90">Send report</button>
              </form>
              <p data-success className="hidden text-sm text-success mt-2">Thanks — we&apos;ll send the report to your inbox within one business day.</p>
              <p data-error className="hidden text-sm text-error mt-2">Submission failed. Please <a href="/contact" className="underline">contact us</a> directly.</p>
            </div>

            <div className="bg-muted/40 border border-border rounded-lg p-3 text-xs text-text-secondary">
              These calculations are estimates based on publicly-available industry benchmarks and the Bootminds team&apos;s 2018–present GCC engagement data. Actual results vary by scope, market conditions, and implementation. We recommend a detailed consultation for precise projections.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROIResults;